import {
  Button, Card, Form, Input, message, Typography,
} from 'antd';
import { resetPasswordConfirm } from 'api/auth';
import Layout from 'components/Layout';
import { AdminContext } from 'constants/contexts';
import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import { WL_CONFIG } from 'constants';

const Logo = WL_CONFIG.logo.medium;

function ForgetPasswordConfirm() {
  const [loading, setLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const { isLoggedIn } = useContext(AdminContext);
  const location = useLocation();
  const { uid, token } = useParams();

  const handleSubmit = (data) => {
    // Validate that we have the required URL parameters
    if (!uid || !token) {
      message.error('Invalid password reset link. Please request a new password reset.');
      return;
    }

    setLoading(true);
    resetPasswordConfirm({ ...data, uid, token })
      .then((res) => {
        message.success('Password successfully changed!');
        setRedirect(true);
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);

        // Handle specific error cases
        const errorMessage = getHttpErrorMessage(e);

        // Check for common password reset errors
        if (e.response?.status === 400) {
          const errorData = e.response?.data;
          if (errorData?.token || errorData?.uid) {
            message.error('This password reset link is invalid or has expired. Please request a new password reset.');
          } else if (errorData?.new_password1 || errorData?.new_password2) {
            message.error('Please check your password requirements and try again.');
          } else {
            message.error(errorMessage || 'Invalid request. Please check your input and try again.');
          }
        } else if (e.response?.status === 404) {
          message.error('Password reset link not found. Please request a new password reset.');
        } else if (e.response?.status >= 500) {
          message.error('Server error. Please try again later.');
        } else {
          message.error(errorMessage || 'An error occurred while resetting your password. Please try again.');
        }
      });
  };

  useEffect(() => {
    setRedirect(isLoggedIn);
  }, [isLoggedIn]);

  useEffect(() => {
    // Validate URL parameters on component mount
    if (!uid || !token) {
      message.error('Invalid password reset link. Please request a new password reset.');
    }
  }, [uid, token]);

  const { from } = location?.state || { from: { pathname: '/console' } };

  if (redirect === true) {
    return <Navigate to={from} />;
  }

  return (
    <Layout hasSidebar={false}>
      <div className="w-screen h-screen flex items-center justify-center">
        <Card className="w-96">
          <div className="flex items-center justify-center mb-5">
            <Logo style={{ width: '100%' }} />
          </div>
          <Typography.Title level={1} className="mt-3" style={{ fontSize: 24 }}>
            Reset Your Password
          </Typography.Title>
          <Typography.Text type="secondary" className="block mb-2">
            Enter your new password below
          </Typography.Text>
          <Typography.Text type="secondary" className="block mb-6 text-xs">
            Password must be at least 8 characters long
          </Typography.Text>
          <Form
            onFinish={handleSubmit}
            className="mt-6"
            validateTrigger="onSubmit"
          >
            <Form.Item
              name="new_password1"
              rules={[
                { required: true, message: 'Please enter your new password!' },
                { min: 8, message: 'Password must be at least 8 characters long!' },
              ]}
            >
              <Input.Password placeholder="New Password" />
            </Form.Item>

            <Form.Item
              name="new_password2"
              dependencies={['new_password1']}
              rules={[
                { required: true, message: 'Please confirm your new password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password1') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm New Password" />
            </Form.Item>

            <Form.Item>
              <Button
                block
                loading={loading}
                disabled={loading}
                type="primary"
                htmlType="submit"
                className="mt-5"
              >
                Reset Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </Layout>
  );
}

export default ForgetPasswordConfirm;
